// eslint-disable-next-line node/no-unpublished-import
import {action} from 'typesafe-actions';
import {ViewMode} from '@taopix/taopix-core';
import {ThreeDPreviewStatus} from '../../../Enums';
import {
  AutoFillOptions,
  SelectedSideData,
  UIFlags,
  UiDialogs,
  UserInterfaceState,
} from './UserInterfaceContext';
import {ToolPaletteState} from './UserInterfaceContext';
import {PageInterface, SortOrder} from '@taopix/taopix-core/dist/types/types';

export enum UserInterfaceActionTypes {
  SET_PAGE_NUMBER = 'SET_PAGE_NUMBER',
  SET_LANGUAGE_DIALOG_OPEN = 'SET_LANGUAGE_DIALOG_OPEN',
  SET_TOOL_PALETTE_SECTION = 'SET_TOOL_PALETTE_SECTION',
  SET_PAGESTYLE_ERROR_TIP = 'SET_PAGESTYLE_ERROR_TIP',
  SET_FOCUS_MODE = 'SET_FOCUS_MODE',
  SET_IS_BUSY = 'SET_IS_BUSY',
  SET_IS_SMALL_SCREEN = 'SET_IS_SMALL_SCREEN',
  SET_VIEW_OFFSET = 'SET_VIEW_OFFSET',
  CALENDAR_EDIT_PICTURE_DIALOG = 'CALENDAR_EDIT_PICTURE_DIALOG',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  SET_3DPREVIEW_STATUS = 'SET_3DPREVIEW_STATUS',
  SET_DIALOG_STATUS = 'SET_DIALOG_STATUS',
  SET_FLAGS = 'SET_FLAGS',
  SET_PALETTE_SECTION_DIRTY = 'SET_PALETTE_SECTION_DIRTY',
  SET_PALETTE_STATE = 'SET_PALETTE_STATE',
  SET_PICTURE_PALETTE_SORTORDER = 'SET_PICTURE_PALETTE_SORTORDER',
  SET_PAGE_LOCK = 'SET_PAGE_LOCK',
  SET_AUTOFILL_OPTIONS = 'SET_AUTOFILL_OPTIONS',
  SET_ZOOM_LEVEL = 'SET_ZOOM_LEVEL',
  SET_PROJECT_NAME_DIALOG = 'SET_PROJECT_NAME_DIALOG',
  SET_SAVE_PROMPT = 'SET_SAVE_PROMPT',
  SET_SAVE_COUNT = 'SET_SAVE_COUNT',
  SET_GRID_SIZE = 'SET_GRID_SIZE',
  SET_AUTO_GRID_SIZE_SWITCHING = 'SET_AUTO_GRID_SIZE_SWITCHING',
  SET_PREVIEW_VIEW_MODE = 'SET_PREVIEW_VIEW_MODE',
  SET_HAS_PROJECT_CHANGED = 'SET_HAS_PROJECT_CHANGED',
  SET_CALENDAR_APPLY_LAYOUTS_WARNING = 'SET_CALENDAR_APPLY_LAYOUTS_WARNING',
  SET_SELECTED_SIDE = 'SET_SELECTED_SIDE',
  SET_TIP_OPEN = 'SET_TIP_OPEN',
  SET_FROM_ASSISTANT = 'SET_FROM_ASSISTANT',
  SET_SWAP_ACTIVE = 'SET_SWAP_ACTIVE',
  SET_HISTORY_STATE = 'SET_HISTORY_STATE',
  RESET_STATE = 'RESET_STATE',
  SET_INSIDE_COVER_NOT_EDITABLE_TIP = 'SET_INSIDE_COVER_NOT_EDITABLE_TIP',
  SET_TEXT_IS_PROCESSING = 'SET_TEXT_IS_PROCESSING',
  SET_KEYBOARD_HELP = 'SET_KEYBOARD_HELP',
  SET_GRID_VIEW_DATA = 'SET_GRID_VIEW_DATA',
}

export const UserInterfaceActions = {
  setPageNumber: (pageNumber: number) =>
    action(UserInterfaceActionTypes.SET_PAGE_NUMBER, pageNumber),
  setLanguageDialogOpen: (truthy: boolean) =>
    action(UserInterfaceActionTypes.SET_LANGUAGE_DIALOG_OPEN, truthy),
  setIsBusy: (busy: boolean) => action(UserInterfaceActionTypes.SET_IS_BUSY, busy),
  setToolPaletteSection: (section: string | null) =>
    action(UserInterfaceActionTypes.SET_TOOL_PALETTE_SECTION, section),
  setPageStyleErrorTip: (payload: {open: boolean; errorString?: string}) =>
    action(UserInterfaceActionTypes.SET_PAGESTYLE_ERROR_TIP, payload),
  setFocusMode: (focusMode: boolean) => action(UserInterfaceActionTypes.SET_FOCUS_MODE, focusMode),
  setIsSmallScreen: (isSmallScreen: boolean) =>
    action(UserInterfaceActionTypes.SET_IS_SMALL_SCREEN, isSmallScreen),
  setViewOffset: (viewOffset: number) =>
    action(UserInterfaceActionTypes.SET_VIEW_OFFSET, viewOffset),
  setCalendarEditPictureDialog: (calendarEditPictureDialog: boolean) =>
    action(UserInterfaceActionTypes.CALENDAR_EDIT_PICTURE_DIALOG, calendarEditPictureDialog),
  setViewMode: (viewMode: ViewMode) => action(UserInterfaceActionTypes.SET_VIEW_MODE, viewMode),
  set3DPreviewStatus: (status: ThreeDPreviewStatus) =>
    action(UserInterfaceActionTypes.SET_3DPREVIEW_STATUS, status),
  setDialogStatus: (dialogStatus: Partial<UiDialogs>) =>
    action(UserInterfaceActionTypes.SET_DIALOG_STATUS, dialogStatus),
  setFlags: (flags: Partial<UIFlags>) => action(UserInterfaceActionTypes.SET_FLAGS, flags),
  setToolPaletteDirty: (dirty: boolean) =>
    action(UserInterfaceActionTypes.SET_PALETTE_SECTION_DIRTY, dirty),
  setToolPalette: (props: Partial<ToolPaletteState>) =>
    action(UserInterfaceActionTypes.SET_PALETTE_STATE, props),
  setPicturePaletteSortOrder: (payload: {sortOrder: SortOrder; sortDirection: 'asc' | 'desc'}) =>
    action(UserInterfaceActionTypes.SET_PICTURE_PALETTE_SORTORDER, payload),
  setPageLock: (truthy: boolean) => action(UserInterfaceActionTypes.SET_PAGE_LOCK, truthy),
  setAutoFillOptions: (payload: Partial<AutoFillOptions>) =>
    action(UserInterfaceActionTypes.SET_AUTOFILL_OPTIONS, payload),
  setCanEditPageLayout: (truthy: boolean) => action(UserInterfaceActionTypes.SET_PAGE_LOCK, truthy),
  setZoomLevel: (zoomLevel: number) => action(UserInterfaceActionTypes.SET_ZOOM_LEVEL, zoomLevel),
  setProjectNameDialog: (payload: {
    previousProjectName: string;
    projectName: string;
    editName: boolean;
  }) => action(UserInterfaceActionTypes.SET_PROJECT_NAME_DIALOG, payload),
  setSavePrompt: (open: boolean) => action(UserInterfaceActionTypes.SET_SAVE_PROMPT, open),
  setSaveCount: (count: number) => action(UserInterfaceActionTypes.SET_SAVE_COUNT, count),
  setGridSize: (gridSize: number | null) =>
    action(UserInterfaceActionTypes.SET_GRID_SIZE, gridSize),
  setAutoGridSizeSwitching: (autoGridSizeSwitching: boolean) =>
    action(UserInterfaceActionTypes.SET_AUTO_GRID_SIZE_SWITCHING, autoGridSizeSwitching),
  setPreviewViewMode: (viewMode: ViewMode) =>
    action(UserInterfaceActionTypes.SET_PREVIEW_VIEW_MODE, viewMode),
  setHasProjectChanged: (projectChanged: boolean) =>
    action(UserInterfaceActionTypes.SET_HAS_PROJECT_CHANGED, projectChanged),
  setCalendarApplyLayoutsWarning: (open: boolean) =>
    action(UserInterfaceActionTypes.SET_CALENDAR_APPLY_LAYOUTS_WARNING, open),
  setSelectedSide: (data: SelectedSideData) =>
    action(UserInterfaceActionTypes.SET_SELECTED_SIDE, data),
  setTipOpen: (payload: {name: string; open: boolean}) =>
    action(UserInterfaceActionTypes.SET_TIP_OPEN, payload),
  setFromAssistant: (payload: {fromAssistant: boolean; assistantRan: boolean}) =>
    action(UserInterfaceActionTypes.SET_FROM_ASSISTANT, payload),
  setSwapActive: (swapActive: boolean) =>
    action(UserInterfaceActionTypes.SET_SWAP_ACTIVE, swapActive),
  setHistoryState: (payload: {undo: boolean; redo: boolean}) =>
    action(UserInterfaceActionTypes.SET_HISTORY_STATE, payload),
  resetState: (payload: UserInterfaceState) =>
    action(UserInterfaceActionTypes.RESET_STATE, payload),
  setInsideCoverNotEditableTip: (payload: {open: boolean; message: string; title: string}) =>
    action(UserInterfaceActionTypes.SET_INSIDE_COVER_NOT_EDITABLE_TIP, payload),
  setTextIsProcessing: (payload: boolean) =>
    action(UserInterfaceActionTypes.SET_TEXT_IS_PROCESSING, payload),
  setKeyboardHelp: (payload: {open: boolean; currentTip: string | null}) =>
    action(UserInterfaceActionTypes.SET_KEYBOARD_HELP, payload),
  setGridViewData: (payload: {columns: number; sortedPages: PageInterface[]}) =>
    action(UserInterfaceActionTypes.SET_GRID_VIEW_DATA, payload),
};
